import { Component, OnInit } from '@angular/core';
import { PLATFORM_ID, Inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { FormControl, FormGroup } from '@angular/forms';
import {
  DataService,
  PamlabApiServiceService,
  RouteService,
  ScreenSizeService,
} from '@/shared/services';
import { isPlatformBrowser } from '@angular/common';
import { Subscription } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { environment } from '../../../../../environments/environment';
import { APRGraduationSummaryFilters } from '../../../../../server/types';
import { View } from '@/core/interfaces';

interface APRGraduation {
  college: string;
  department: string;
  program: string;
  hs_gpa_lower: number;
  hs_gpa_upper: number;
  cohort: number | string;
  demographic: string;
}

@Component({
  selector: 'apr-graduation-summary-form',
  templateUrl: './apr-graduation-summary-form.component.html',
  styleUrls: ['./apr-graduation-summary-form.component.css'],
  standalone: false,
})
export class AprGraduationSummaryFormComponent implements OnInit {
  private subscription: Subscription = new Subscription();
  public departmentOptions: string[] = [];
  public programOptions: string[] = [];
  public view!: View;

  // apr form group
  public aprGraduationSummaryForm = new FormGroup({
    college: new FormControl(''),
    department: new FormControl(''),
    program: new FormControl(''),
    demographic: new FormControl(''),
    hs_gpa_lower: new FormControl(0),
    hs_gpa_upper: new FormControl(5.04),
    cohort: new FormControl(2020),
  });

  // variable to check if user form submission is same as previous submission
  prevParam: APRGraduation = {
    college: '',
    department: '',
    program: '',
    hs_gpa_lower: 0,
    hs_gpa_upper: 0, //5.04
    demographic: '',
    cohort: 0,
  };

  filters: APRGraduationSummaryFilters = {
    cohorts: [],
    colleges: [],
    demographics: [],
    departments: [],
    programs: [],
  };
  filtersInitial!: APRGraduationSummaryFilters;

  constructor(
    private _dialog: MatDialog,
    private _apiService: PamlabApiServiceService,
    private _dataService: DataService,
    private _routeService: RouteService,
    private screenSizeService: ScreenSizeService,
    @Inject(PLATFORM_ID) private platformId: object,
  ) {}

  ngOnInit(): void {
    this.subscription.add(
      this.screenSizeService.screenView$.subscribe((view) => {
        this.view = view;
      }),
    );

    this.subscription.add(
      this._apiService.fetchAPRGraduationSummaryFilters().subscribe({
        next: (filters) => {
          this.filtersInitial = JSON.parse(JSON.stringify(filters));
          this.filters = filters;
          this.filters.departments = [];
          this.filters.programs = [];
          this.view === 'web' && this.aprGraduationSummarySubmit();
        },
      }),
    );
  }

  aprGraduationSummarySubmit() {
    this._dataService.setPendingData(true);

    // calling _apiService to get student information based on form inputs.
    this.subscription.add(
      this._apiService
        .getAPRTrendsWithParams({
          college: this.aprGraduationSummaryForm.value.college!,
          department: this.aprGraduationSummaryForm.value.department!,
          program: this.aprGraduationSummaryForm.value.program!,
          hs_gpa_lower: this.aprGraduationSummaryForm.value.hs_gpa_lower!,
          hs_gpa_upper: this.aprGraduationSummaryForm.value.hs_gpa_upper!,
          cohort: this.aprGraduationSummaryForm.value.cohort!,
          demographic: this.aprGraduationSummaryForm.value.demographic!,
        })
        .subscribe({
          next: (data) => {
            this._dataService.setData({ data });
            this._dataService.setPendingData(false);
          },
          error: (errorRes: HttpErrorResponse) => {
            if (errorRes.status === 401) {
              this._routeService.navigate(environment['login_url']);
            }
            this._dataService.setData({ error: errorRes.error });
            this._dataService.setPendingData(false);
          },
        }),
    );
  }

  ngOnDestroy(): void {
    setTimeout(() => {
      this.subscription.unsubscribe();
    }, 3000);
  }

  // to check if user entered same form value from previous submission
  sameParams() {
    return (
      this.prevParam &&
      this.prevParam.college === this.aprGraduationSummaryForm.value.college &&
      this.prevParam.department ===
        this.aprGraduationSummaryForm.value.department &&
      this.prevParam.program === this.aprGraduationSummaryForm.value.program &&
      this.prevParam.hs_gpa_lower ===
        this.aprGraduationSummaryForm.value.hs_gpa_lower &&
      this.prevParam.hs_gpa_upper ===
        this.aprGraduationSummaryForm.value.hs_gpa_upper &&
      this.prevParam.demographic ===
        this.aprGraduationSummaryForm.value.demographic &&
      this.prevParam.cohort === this.aprGraduationSummaryForm.value.cohort
    );
  }

  // To determine what college user choose to change program option for form field
  onCollegeChange() {
    const college = this.aprGraduationSummaryForm.value.college;
    this.aprGraduationSummaryForm.patchValue({ department: '', program: '' });

    if (!college) {
      this.filters.departments = [];
    } else {
      this.filters.departments = this.filtersInitial.departments.filter(
        (x) => x.college === college,
      );
    }
    this.filters.programs = [];
  }

  onDepartmentChange() {
    const department = this.aprGraduationSummaryForm.value.department;
    const college = this.aprGraduationSummaryForm.value.college;
    this.aprGraduationSummaryForm.patchValue({ program: '' });

    if (!department) {
      this.filters.programs = [];
    } else {
      this.filters.programs = this.filtersInitial.programs.filter(
        (x) => x.department === department && x.college === college!,
      );
    }
  }

  // close popup _dialog after form is submitted
  closeDialog() {
    this._dialog.closeAll();
  }

  // Error message when user leaves form fields empty
  getErrorMessage() {
    return 'You must enter a value';
  }

  isBrowser() {
    return isPlatformBrowser(this.platformId);
  }
}
